import type {
  EvolutionConnectionState,
  EvolutionInstancePairing,
  UpdateWatiConfigInput,
  WatiConfig,
  WatiMessageLogEntry,
  WatiSweepResult,
  WatiTemplate,
} from '@pms/shared';
import { api } from '@/lib/api';

interface DataResponse<T> {
  data: T;
}

/** The config plus whether the server actually has WATI credentials in its env. */
export type WatiConfigResponse = WatiConfig & { is_configured: boolean };

export interface WatiTestSendResult {
  ok: boolean;
  info: string | null;
  to: string;
}

export interface EvolutionStatus {
  configured: boolean;
  /** Null when unconfigured. Only 'open' can send. */
  state: EvolutionConnectionState | null;
  instance_name: string;
}

export const messageService = {
  async getConfig(): Promise<WatiConfigResponse> {
    const res = await api.get<DataResponse<WatiConfigResponse>>('/messages/config');
    return res.data.data;
  },

  async updateConfig(input: UpdateWatiConfigInput): Promise<void> {
    await api.patch('/messages/config', input);
  },

  /** `refresh` bypasses the server's 10-minute cache after approving a template in WATI. */
  async listTemplates(refresh = false): Promise<WatiTemplate[]> {
    const res = await api.get<DataResponse<WatiTemplate[]>>('/messages/templates', {
      params: refresh ? { refresh: 'true' } : undefined,
    });
    return res.data.data;
  },

  /** Live WhatsApp session state. Deliberately uncached — a dropped socket must show. */
  async getEvolutionStatus(): Promise<EvolutionStatus> {
    const res = await api.get<DataResponse<EvolutionStatus>>('/messages/evolution/status');
    return res.data.data;
  },

  /**
   * Creates the instance on the Evolution server and returns its first QR. Does NOT make it
   * the active instance — that is a separate updateConfig once pairing succeeds.
   */
  async createEvolutionInstance(instanceName: string): Promise<EvolutionInstancePairing> {
    const res = await api.post<DataResponse<EvolutionInstancePairing>>(
      '/messages/evolution/instances',
      { instance_name: instanceName },
    );
    return res.data.data;
  },

  /** A fresh QR — Baileys rotates the code, so this is re-read rather than held open. */
  async getEvolutionInstanceQr(instanceName: string): Promise<EvolutionInstancePairing> {
    const res = await api.get<DataResponse<EvolutionInstancePairing>>(
      `/messages/evolution/instances/${encodeURIComponent(instanceName)}/qr`,
    );
    return res.data.data;
  },

  async getEvolutionInstanceState(
    instanceName: string,
  ): Promise<{ instance_name: string; state: EvolutionConnectionState }> {
    const res = await api.get<DataResponse<{ instance_name: string; state: EvolutionConnectionState }>>(
      `/messages/evolution/instances/${encodeURIComponent(instanceName)}/state`,
    );
    return res.data.data;
  },

  /** Cleanup for an abandoned pairing. The server refuses the currently-active instance. */
  async deleteEvolutionInstance(instanceName: string): Promise<void> {
    await api.delete(`/messages/evolution/instances/${encodeURIComponent(instanceName)}`);
  },

  async testSend(phone: string): Promise<WatiTestSendResult> {
    const res = await api.post<DataResponse<WatiTestSendResult>>('/messages/test-send', { phone });
    return res.data.data;
  },

  async runNow(): Promise<WatiSweepResult> {
    const res = await api.post<DataResponse<WatiSweepResult>>('/messages/run-now');
    return res.data.data;
  },

  async getLog(limit = 50): Promise<WatiMessageLogEntry[]> {
    const res = await api.get<DataResponse<WatiMessageLogEntry[]>>('/messages/log', { params: { limit } });
    return res.data.data;
  },
};
